/**
 * Orchestrator hook for the brief generation flow.
 *
 * Owns the state machine + context minting + uploads + persistence. Does
 * NOT call `useChat` directly — Refinement 2 from the design splits the
 * stream into a child component (`<BriefGenerationStream>`) that consumes
 * the `streamProps` object exposed here. The child fans events back to
 * the hook via the callbacks on `streamProps`.
 *
 * Lifecycle:
 *   idle → preparing → uploading → streaming → persisting → success
 *                                                       └→ error
 *
 * Cleanup contract: this hook intentionally does NOT use `useEffect` for
 * cleanup-on-unmount. The consuming modal owns the lifecycle — when the
 * modal unmounts (dismissal, navigation away) it MUST call `cancel()` so
 * the in-flight `AbortController` is aborted and downstream state is
 * cleared. This keeps the hook free of effects per the project's
 * no-direct-useEffect rule.
 */
import { useCallback, useReducer, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useBooleanFlagValue } from '@openfeature/react-sdk';

import type { Brief } from '@/entities/brief';
import { useCreateBriefMutation } from '@/entities/brief';
import { useCurrentWorkspaceId } from '@/entities/workspace';
import type { CreateBriefDto } from '@inference.engine/workspace-api-client/web';
import { createChatId } from '@/shared/api/agent-runner';
import { notificationService } from '@/lib/services/notification.service';
import { logger } from '@/lib/utils/logger';

import { matchPipelineStep, matchPipelineSteps } from '../../lib/pipeline-steps';
import { uploadIntakeFiles, type IntakeUploadedAsset } from '../../lib/upload-intake-files';
import {
  BRIEF_GENERATION_AGENT_ID,
  BRIEF_GENERATION_WORKSPACE_API_FLAG,
  PIPELINE_STEPS,
} from '../brief-generation.constants';
import type {
  BriefSubmitPayload,
  GenerationStatus,
  PipelineStep,
  PipelineStepId,
  PipelineStepState,
} from '../brief-generation.types';

type StepsMap = Record<PipelineStepId, PipelineStepState>;

interface CapturedWidget {
  brief: CreateBriefDto;
}

interface State {
  status: GenerationStatus;
  steps: StepsMap;
  error: Error | null;
  brief: Brief | null;
  contextId: string | null;
  payload: BriefSubmitPayload | null;
  uploadedAssets: IntakeUploadedAsset[];
  capturedWidget: CapturedWidget | null;
}

type Action =
  | { type: 'start'; payload: BriefSubmitPayload }
  | { type: 'context-minted'; contextId: string }
  | { type: 'context-skipped' }
  | { type: 'uploads-done'; assets: IntakeUploadedAsset[] }
  | { type: 'step-running'; id: PipelineStepId }
  | { type: 'step-done'; id: PipelineStepId }
  | { type: 'step-error'; id: PipelineStepId }
  | { type: 'widget-captured'; widget: CapturedWidget }
  | { type: 'persist-success'; brief: Brief }
  | { type: 'persist-error'; error: Error }
  | { type: 'flow-error'; error: Error }
  | { type: 'reset' };

const ALL_PENDING_STEPS: StepsMap = PIPELINE_STEPS.reduce<StepsMap>((acc, step) => {
  acc[step.id] = 'pending';
  return acc;
}, {} as StepsMap);

const INITIAL_STATE: State = {
  status: 'idle',
  steps: ALL_PENDING_STEPS,
  error: null,
  brief: null,
  contextId: null,
  payload: null,
  uploadedAssets: [],
  capturedWidget: null,
};

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'start':
      return { ...INITIAL_STATE, status: 'preparing', payload: action.payload };
    case 'context-minted':
      return { ...state, contextId: action.contextId, status: 'uploading' };
    case 'context-skipped':
      return { ...state, status: 'uploading' };
    case 'uploads-done':
      return { ...state, uploadedAssets: action.assets, status: 'streaming' };
    /* eslint-disable i18next/no-literal-string -- step status values */
    case 'step-running':
      return { ...state, steps: { ...state.steps, [action.id]: 'running' } };
    case 'step-done':
      return { ...state, steps: { ...state.steps, [action.id]: 'done' } };
    case 'step-error':
      return {
        ...state,
        steps: { ...state.steps, [action.id]: 'error' },
        status: 'error',
      };
    /* eslint-enable i18next/no-literal-string */
    case 'widget-captured':
      // Refinement 3: atomically mark composing-brief done AND transition to
      // persisting AND mark saving-brief running — no intermediate render
      // where step 5 is still "running" while the persist mutation has
      // already started.
      return {
        ...state,
        steps: {
          ...state.steps,
          'composing-brief': 'done',
          'saving-brief': 'running',
        },
        status: 'persisting',
        capturedWidget: action.widget,
      };
    case 'persist-success':
      return {
        ...state,
        steps: { ...state.steps, 'saving-brief': 'done' },
        status: 'success',
        brief: action.brief,
      };
    case 'persist-error':
      return {
        ...state,
        steps: { ...state.steps, 'saving-brief': 'error' },
        status: 'error',
        error: action.error,
      };
    case 'flow-error':
      return { ...state, status: 'error', error: action.error };
    case 'reset':
      return INITIAL_STATE;
  }
};

export interface UseBriefGenerationOptions {
  projectId: string;
  /**
   * Called once after the mutation persists the brief successfully. The
   * consuming modal typically uses this to navigate to the brief detail
   * page and close itself.
   */
  onSuccess?: (brief: Brief) => void;
}

/**
 * Props consumed by the `<BriefGenerationStream>` child.
 *
 * Only populated once the context has been minted, uploads have settled,
 * and the orchestrator has transitioned to the `streaming` state. The
 * consuming modal renders the child only when this is non-null.
 */
export interface BriefGenerationStreamProps {
  /**
   * The reasoner context this run belongs to, absent on the workspace-api path. That host is
   * stateless and returns the brief as the run's output, so its presence is what tells the stream
   * which host to post to.
   */
  contextId?: string;
  agentId: string;
  workspaceId: string;
  payload: BriefSubmitPayload;
  uploadedAssets: IntakeUploadedAsset[];
  abortSignal: AbortSignal;
  onStepEvent: (kind: 'start' | 'complete' | 'error', identifier: string) => void;
  onWidgetCaptured: (widget: CapturedWidget) => void;
  onStreamError: (error: Error) => void;
}

export interface UseBriefGenerationReturn {
  status: GenerationStatus;
  /**
   * Ordered pipeline steps with their current state merged in. Always
   * 6 entries (5 BE-driven + 1 FE-only `saving-brief`).
   */
  steps: (PipelineStep & { state: PipelineStepState })[];
  error: Error | null;
  brief: Brief | null;
  start: (payload: BriefSubmitPayload) => Promise<void>;
  cancel: () => void;
  /**
   * Re-fire the persist mutation with the captured widget payload.
   * Only meaningful when status is `error` AND the failure originated in
   * the persist phase (i.e. `capturedWidget !== null`). No-op otherwise.
   */
  retry: () => void;
  reset: () => void;
  /** Props for `<BriefGenerationStream>`. Null until the orchestrator is in `streaming`. */
  streamProps: BriefGenerationStreamProps | null;
}

export const useBriefGeneration = ({
  projectId,
  onSuccess,
}: UseBriefGenerationOptions): UseBriefGenerationReturn => {
  const { t } = useTranslation('briefs');
  const [state, dispatch] = useReducer(reducer, INITIAL_STATE);
  const abortRef = useRef<AbortController | null>(null);
  const workspaceId = useCurrentWorkspaceId();
  const mutation = useCreateBriefMutation({ projectId });
  const useWorkspaceApi = useBooleanFlagValue(BRIEF_GENERATION_WORKSPACE_API_FLAG, false);

  const fireMutate = useCallback(
    (brief: CreateBriefDto, signal: AbortSignal | undefined) => {
      mutation.mutate(
        { body: brief, signal },
        {
          onSuccess: (persisted) => {
            dispatch({ type: 'persist-success', brief: persisted });
            onSuccess?.(persisted);
          },
          onError: (error) => {
            dispatch({ type: 'persist-error', error });
          },
        },
      );
    },
    [mutation, onSuccess],
  );

  const start = useCallback(
    async (payload: BriefSubmitPayload) => {
      // New run = new controller. Any prior in-flight controller is
      // intentionally orphaned because cancel/reset already handles
      // tear-down on the consumer side.
      const controller = new AbortController();
      abortRef.current = controller;
      dispatch({ type: 'start', payload });

      try {
        // No context on the workspace-api path: that host is stateless and returns the brief as
        // the run's output, so a minted chat context would be an empty conversation nothing reads.
        if (useWorkspaceApi) {
          dispatch({ type: 'context-skipped' });
        } else {
          const context = await createChatId(BRIEF_GENERATION_AGENT_ID, payload.title, true);
          if (controller.signal.aborted) return;
          dispatch({ type: 'context-minted', contextId: context.id });
        }

        const uploadResults = await uploadIntakeFiles(payload.files, controller.signal);
        // The signal can flip between awaits when the consumer calls `cancel()` mid-flow; this
        // defensive check stops us from dispatching post-cancel state.
        if (controller.signal.aborted) return;

        const successfulAssets = uploadResults.flatMap((r) => (r.asset ? [r.asset] : []));
        const allFailed = payload.files.length > 0 && successfulAssets.length === 0;
        if (allFailed) {
          const error = new Error('All file uploads failed');
          dispatch({ type: 'flow-error', error });
          notificationService.showError(
            t('briefGeneration.useBriefGeneration.failedToUploadFilesPlease'),
          );
          return;
        }

        dispatch({ type: 'uploads-done', assets: successfulAssets });
      } catch (cause) {
        const error = cause instanceof Error ? cause : new Error(String(cause));
        if (error.name === 'AbortError') {
          // User-driven abort — `cancel()` already cleaned up state.
          return;
        }
        dispatch({ type: 'flow-error', error });
        notificationService.showError(
          error.message || t('briefGeneration.useBriefGeneration.failedToStartBriefGeneration'),
        );
      }
    },
    [t],
  );

  const cancel = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    dispatch({ type: 'reset' });
  }, []);

  const reset = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    dispatch({ type: 'reset' });
    mutation.reset();
  }, [mutation]);

  const retry = useCallback(() => {
    if (!state.capturedWidget) return;
    fireMutate(state.capturedWidget.brief, abortRef.current?.signal);
  }, [state.capturedWidget, fireMutate]);

  /**
   * The identifier is a localized step label from the original runtime or an agent id from Relay,
   * so both matchers are tried. One agent drives two steps, hence a list rather than one id.
   */
  const handleStepEvent = useCallback(
    (kind: 'start' | 'complete' | 'error', identifier: string) => {
      const labelMatch = matchPipelineStep(identifier);
      const ids = labelMatch ? [labelMatch] : matchPipelineSteps(identifier);
      if (ids.length === 0) {
        logger.debug('useBriefGeneration: unrecognised pipeline step', { identifier });
        return;
      }
      for (const id of ids) {
        if (kind === 'start') {
          dispatch({ type: 'step-running', id });
        } else if (kind === 'complete') {
          dispatch({ type: 'step-done', id });
        } else {
          dispatch({ type: 'step-error', id });
        }
      }
    },
    [],
  );

  const handleWidgetCaptured = useCallback(
    (widget: CapturedWidget) => {
      dispatch({ type: 'widget-captured', widget });
      fireMutate(widget.brief, abortRef.current?.signal);
    },
    [fireMutate],
  );

  const handleStreamError = useCallback(
    (error: Error) => {
      dispatch({ type: 'flow-error', error });
      notificationService.showError(
        error.message || t('briefGeneration.useBriefGeneration.briefGenerationStreamFailed'),
      );
    },
    [t],
  );

  // Derived ordered steps with state merged in (rendered top→bottom by consumer).
  const steps = PIPELINE_STEPS.map((step) => ({
    ...step,
    state: state.steps[step.id],
  }));

  // streamProps populated only once the orchestrator is in `streaming` AND
  // the workspace id is known. Without a workspace we cannot mint the
  // `x-selected-workspace-id` header the BE requires for the stream.
  //
  // A context id is required on the reasoner path and absent on the workspace-api one, which is
  // what tells the stream which host it is talking to.
  const streamProps: BriefGenerationStreamProps | null =
    state.status === 'streaming' &&
    (useWorkspaceApi || state.contextId !== null) &&
    state.payload !== null &&
    abortRef.current !== null &&
    workspaceId !== null
      ? {
          ...(state.contextId === null ? {} : { contextId: state.contextId }),
          agentId: BRIEF_GENERATION_AGENT_ID,
          workspaceId,
          payload: state.payload,
          uploadedAssets: state.uploadedAssets,
          abortSignal: abortRef.current.signal,
          onStepEvent: handleStepEvent,
          onWidgetCaptured: handleWidgetCaptured,
          onStreamError: handleStreamError,
        }
      : null;

  return {
    status: state.status,
    steps,
    error: state.error,
    brief: state.brief,
    start,
    cancel,
    retry,
    reset,
    streamProps,
  };
};
